/**
 * comments-http — live HTTP implementation of :class:`CommentsPort`.
 *
 * Wraps the three review-comment endpoints (all **snake_case** on the wire)
 * under `/api/v1/case-ecritures/{ecriture_id}` — the brief IS a case-écriture,
 * so `ecritureId` is the active `briefId`:
 *   - `GET  /threads?resolved=<bool>`            → `ThreadListResponse{threads}`
 *   - `POST /threads/{thread_id}/comments`       → `CommentResponse`
 *   - `PATCH /threads/{thread_id}/resolve`       → `ThreadResponse`
 *
 * Hand-rolled validation (no zod / no codegen) per the case-http / auth-http
 * precedent. W1/W2: pure field projection (snake→camel), no business logic,
 * no Office.js. The open/resolved filter maps 1:1 to the `resolved` query
 * param — the filter is a SERVER decision, never computed locally.
 */

import type { CommentsPort } from '@/domain/comments/ports'
import type {
  CommentThread,
  ThreadComment,
  ThreadFilter,
  ThreadPage,
} from '@/domain/comments/entity'
import type { HttpErrorKind } from './_errors'

export interface CommentsHttpConfig {
  baseUrl: string
  getAccessToken: () => string | null
  fetch?: typeof fetch
}

class CommentsHttpError extends Error {
  constructor(
    public readonly kind: HttpErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'CommentsHttpError'
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function readNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function readBoolean(v: unknown): boolean {
  return v === true
}

/** Read a nullable string field. The BE `anchor_text` is non-null on the wire
 *  (min_length 1), but a defensive `null` keeps the UI quote-block honest if a
 *  future/empty value slips through. */
function readNullableString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function mapComment(raw: unknown): ThreadComment {
  const w = isPlainObject(raw) ? raw : {}
  return {
    id: readString(w.id),
    body: readString(w.body),
    authorName: readString(w.author_name),
    createdAt: readString(w.created_at),
  }
}

function mapThread(raw: unknown): CommentThread {
  const w = isPlainObject(raw) ? raw : {}
  const comments = Array.isArray(w.comments) ? w.comments.map(mapComment) : []
  return {
    id: readString(w.id),
    isResolved: readBoolean(w.is_resolved),
    authorName: readString(w.author_name),
    anchorText: readNullableString(w.anchor_text),
    anchorLost: readBoolean(w.anchor_lost),
    commentCount: readNumber(w.comment_count),
    createdAt: readString(w.created_at),
    comments,
  }
}

/** The thread page size (F-COMMENTS-PAGINATION). One page per `listThreads`
 *  call; the hook accumulates pages via `loadMore`, paging by `offset`. Also
 *  the default `limit` when the port is called without an explicit page. */
const PAGE_SIZE = 50

/** Build the list query: the open/resolved/all filter → the BE `resolved`
 *  query param (`open`→`false`, `resolved`→`true`, `all`→omitted), plus the
 *  `offset`/`limit` window so the list pages instead of silently truncating. */
function listQuery(
  filter: ThreadFilter,
  page: { offset: number; limit: number },
): string {
  const params = new URLSearchParams({
    offset: String(page.offset),
    limit: String(page.limit),
  })
  if (filter === 'open') params.set('resolved', 'false')
  if (filter === 'resolved') params.set('resolved', 'true')
  return `?${params.toString()}`
}

export function createCommentsHttp(config: CommentsHttpConfig): CommentsPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))

  async function request(
    path: string,
    init: { method: string; body?: unknown },
  ): Promise<unknown> {
    const token = config.getAccessToken()
    const headers: Record<string, string> = {}
    if (init.body !== undefined) headers['Content-Type'] = 'application/json'
    if (token) headers['Authorization'] = `Bearer ${token}`

    let res: Response
    try {
      res = await doFetch(`${config.baseUrl}${path}`, {
        method: init.method,
        headers,
        credentials: 'include',
        body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      })
    } catch (err) {
      throw new CommentsHttpError(
        'network',
        err instanceof Error ? err.message : 'Réseau injoignable',
      )
    }
    if (res.status === 401) {
      throw new CommentsHttpError('auth', 'Session expirée — reconnectez-vous')
    }
    if (res.status === 429) {
      throw new CommentsHttpError('rate_limited', 'Trop de requêtes — patientez')
    }
    if (!res.ok) {
      throw new CommentsHttpError('server', `Requête échouée (HTTP ${res.status})`)
    }
    try {
      return await res.json()
    } catch (err) {
      throw new CommentsHttpError(
        'malformed',
        err instanceof Error ? `JSON invalide: ${err.message}` : 'JSON invalide',
      )
    }
  }

  return {
    async listThreads(
      ecritureId: string,
      filter: ThreadFilter,
      page: { offset: number; limit: number } = { offset: 0, limit: PAGE_SIZE },
    ): Promise<ThreadPage> {
      const raw = await request(
        `/api/v1/case-ecritures/${encodeURIComponent(ecritureId)}/threads${listQuery(filter, page)}`,
        { method: 'GET' },
      )
      const rawThreads =
        isPlainObject(raw) && Array.isArray(raw.threads) ? raw.threads : []
      const threads = rawThreads.map(mapThread)
      // `total` is the BE's full count for the filter; fall back to the page
      // length so a wire without it degrades to "this is the whole list".
      const total =
        isPlainObject(raw) && typeof raw.total === 'number'
          ? readNumber(raw.total)
          : threads.length
      return { threads, total }
    },

    async addComment(
      ecritureId: string,
      threadId: string,
      body: string,
    ): Promise<ThreadComment> {
      const raw = await request(
        `/api/v1/case-ecritures/${encodeURIComponent(ecritureId)}/threads/${encodeURIComponent(threadId)}/comments`,
        { method: 'POST', body: { body } },
      )
      return mapComment(raw)
    },

    async resolveThread(
      ecritureId: string,
      threadId: string,
      isResolved: boolean,
    ): Promise<CommentThread> {
      const raw = await request(
        `/api/v1/case-ecritures/${encodeURIComponent(ecritureId)}/threads/${encodeURIComponent(threadId)}/resolve`,
        { method: 'PATCH', body: { is_resolved: isResolved } },
      )
      return mapThread(raw)
    },
  }
}

export { CommentsHttpError, PAGE_SIZE }
